import { getAuthHeaders } from './authToken';

const API_BASE = process.env.REACT_APP_API_URL;

// Delegates to the shared helper so every call carries the signed-in user's Zitadel access token.
const getHeaders = (includeContentType = true) => getAuthHeaders(includeContentType);

/**
 * Get all slack channel configurations
 * @returns {Promise<Array<{id: number, channelId: string, channelName: string, ccEmails: string, isActive: boolean, createdAt: string, updatedAt: string}>>}
 */
export const getSlackChannelConfigurations = async () => {
  const response = await fetch(`${API_BASE}/api/slack-channel-configurations`, {
    method: 'GET',
    headers: getHeaders(false),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch slack channel configurations');
  }

  return response.json();
};

/**
 * Get a single slack channel configuration by ID
 * @param {number} id - Configuration ID
 * @returns {Promise<{id: number, channelId: string, channelName: string, ccEmails: string, isActive: boolean, createdAt: string, updatedAt: string}>}
 */
export const getSlackChannelConfigurationById = async (id) => {
  const response = await fetch(`${API_BASE}/api/slack-channel-configurations/${id}`, {
    method: 'GET',
    headers: getHeaders(false),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch slack channel configuration');
  }

  return response.json();
};

/**
 * Create a new slack channel configuration
 * @param {Object} data - Configuration data
 * @param {string} data.channelId - Slack channel ID
 * @param {string} data.channelName - Channel display name
 * @param {string} data.ccEmails - Comma-separated CC emails
 * @param {boolean} data.isActive - Whether the channel is active
 * @returns {Promise<Object>}
 */
export const createSlackChannelConfiguration = async (data) => {
  const response = await fetch(`${API_BASE}/api/slack-channel-configurations`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to create slack channel configuration');
  }

  return response.json();
};

/**
 * Update an existing slack channel configuration
 * @param {number} id - Configuration ID
 * @param {Object} data - Configuration data
 * @param {string} data.channelId - Slack channel ID
 * @param {string} data.channelName - Channel display name
 * @param {string} data.ccEmails - Comma-separated CC emails
 * @param {boolean} data.isActive - Whether the channel is active
 * @returns {Promise<Object>}
 */
export const updateSlackChannelConfiguration = async (id, data) => {
  const response = await fetch(`${API_BASE}/api/slack-channel-configurations/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error('Failed to update slack channel configuration');
  }

  return response.json();
};

/**
 * Delete a slack channel configuration
 * @param {number} id - Configuration ID
 * @returns {Promise<void>}
 */
export const deleteSlackChannelConfiguration = async (id) => {
  const response = await fetch(`${API_BASE}/api/slack-channel-configurations/${id}`, {
    method: 'DELETE',
    headers: getHeaders(false),
  });

  if (!response.ok) {
    throw new Error('Failed to delete slack channel configuration');
  }
};
